import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: true,
};

// Font stacks for different options
const fontStacks = {
  inter: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  outfit: 'Outfit, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  geist: 'Geist, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
  figtree: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
};

const theme = extendTheme({
  config,
  fonts: {
    heading: fontStacks.inter,
    body: fontStacks.inter,
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
    '9xl': '8rem',
  },
  fontWeights: {
    hairline: 100,
    thin: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  lineHeights: {
    normal: 'normal',
    none: 1,
    shorter: 1.25,
    short: 1.375,
    base: 1.5,
    tall: 1.625,
    taller: '2',
  },
  letterSpacings: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
  colors: {
    brand: {
      50: '#e8f5e8',
      100: '#c3e6c3',
      200: '#9dd69d',
      300: '#77c677',
      400: '#51b651',
      500: '#2ba62b',
      600: '#228522',
      700: '#1a641a',
      800: '#114311',
      900: '#082208',
    },
    primary: {
      50: '#e8f5e8',
      100: '#c3e6c3',
      200: '#9dd69d',
      300: '#77c677',
      400: '#51b651',
      500: '#2ba62b',
      600: '#228522',
      700: '#1a641a',
      800: '#114311',
      900: '#082208',
    },
  },
  styles: {
    global: (props: any) => ({
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : 'gray.50',
        color: props.colorMode === 'dark' ? 'white' : 'gray.800',
        fontFeatureSettings: '"rlig" 1, "calt" 1',
        fontVariantLigatures: 'common-ligatures',
        textRendering: 'optimizeLegibility',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      'h1, h2, h3, h4, h5, h6': {
        fontWeight: 'semibold',
        lineHeight: 'shorter',
      },
      h1: {
        fontSize: '2xl',
        fontWeight: 'bold',
      },
      h2: {
        fontSize: 'xl',
        fontWeight: 'semibold',
      },
      h3: {
        fontSize: 'lg',
        fontWeight: 'semibold',
      },
      p: {
        lineHeight: 'base',
      },
      'a': {
        color: props.colorMode === 'dark' ? 'primary.300' : 'primary.500',
        textDecoration: 'none',
        _hover: {
          textDecoration: 'underline',
        },
      },
    }),
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'primary',
      },
      baseStyle: {
        fontWeight: 'medium',
        borderRadius: 'md',
      },
    },
    Card: {
      baseStyle: (props: any) => ({
        container: {
          bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
          borderColor: props.colorMode === 'dark' ? 'gray.700' : 'gray.200',
          borderRadius: 'lg',
          shadow: 'sm',
        },
      }),
    },
    Input: {
      defaultProps: {
        focusBorderColor: 'primary.500',
      },
    },
    Textarea: {
      defaultProps: {
        focusBorderColor: 'primary.500',
      },
    },
    Select: {
      defaultProps: {
        focusBorderColor: 'primary.500',
      },
    },
    Text: {
      baseStyle: {
        lineHeight: 'base',
      },
    },
    Heading: {
      baseStyle: {
        fontWeight: 'semibold',
        lineHeight: 'shorter',
      },
    },
  },
  textStyles: {
    h1: {
      fontSize: ['2xl', '3xl', '4xl'],
      fontWeight: 'bold',
      lineHeight: 'shorter',
    },
    h2: {
      fontSize: ['xl', '2xl', '3xl'],
      fontWeight: 'semibold',
      lineHeight: 'shorter',
    },
    h3: {
      fontSize: ['lg', 'xl', '2xl'],
      fontWeight: 'semibold',
      lineHeight: 'short',
    },
    h4: {
      fontSize: ['md', 'lg', 'xl'],
      fontWeight: 'semibold',
      lineHeight: 'short',
    },
    body: {
      fontSize: 'md',
      lineHeight: 'base',
    },
    caption: {
      fontSize: 'sm',
      lineHeight: 'base',
      color: 'gray.600',
    },
  },
});

export default theme;